import { Component } from "react";
import { IndividualList } from "../../models/IndividualSelectModel";
import { MerchantPaymentMethod } from "../../models/MerchantModel";
import {
    BankTransferInitiateRequestModel,
    BankTransferRequestModel,
    BankTransferResponseCode,
    CalculateMoneyTransferChargeRequest,
    MoneyTransferRequestDetails,
    NetworkType,
    PaymentType,
    PointToPointMoneyTransferRequestModel,
    ServiceClients,
    TennEntityType,
    TransferType,
    ValidateIbanRequest,
} from "../../models/MoneyTransferModal";
import { ExtendedSelectItem, SelectItem } from "../../models/SelectItem";
import { Amount_Valid_REGEX, AppConstants, Notes_And_Reason_Length, Withdraw_Max_Amount } from "../../models/AppConstants";
import { utils } from "../../utils/utils";
import * as Yup from "yup";
import { ErrorMessage, Formik, FormikHelpers } from "formik";
import { MerchantWalletBalanceRequest, Wallets } from "../../models/FinancialData";
import { KycDocument } from "../../models/DocumentFile";
import { TabPanel, TabView, TabViewTabChangeEvent } from "primereact/tabview";
import { Button } from "primereact/button";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { AppCard, LoadingDots, createTabHeaderTemplate } from "../../components/ui";
import { Dropdown } from "primereact/dropdown";
import { merchantWalletApiService } from "../../services/api/merchants/MerchantWalletApiService";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { storageService } from "../../services/StorageService";
import React from "react";
import { ArrowLeft } from "lucide-react";

import { userWalletApiService } from "../../services/api/users/UserWalletApiService";
import { userApiService } from "../../services/api/users/UsersApiService";
import { OperatorDataModel } from "../../models/User";
import { InputText } from "primereact/inputtext";
import { merchantPaymentApiService } from "../../services/api/merchants/MerchantPaymentApiService";
import { fileService } from "../../services/FileService";
import TransferFundsConfirmDialogue from "./TransferFundsConfirmDialogue";
import { moneyTransferApiService } from "../../services/api/merchants/MoneyTransferApiService";
import { toastService } from "../../services/toastService";
import MoneyTransferSuccessDialog from "./MoneyTransferSuccessDialog";
import UploadSourceOfFundsDialog from "./UploadSourceOfFundsDialog";
import { EntityType, SourceOfFundsUploadRequest } from "../../models/Orchestrator";

interface TransferFundsDialogueProps {
    loggedUserData: OperatorDataModel | null;
    sourceAccountsOptions: ExtendedSelectItem[];
    sourceWallets: Wallets[];
    merchantId: number;
    onHide: () => void;
    reloadData: () => void;
}

interface TransferFundsModal {
    selectsourceAccount: string;
    selectSourceIBANP2Any: string;
    selectbeneficiaryAccount: string;
    selectbeneficiaryIBANAccount: string;
    amount: string;
    paymentnote: string;
    amountP2Any: string;
    paymentnoteP2Any: string;
    beneficiaryName: string;
    beneficiaryIBANAccount: string;
    beneficiaryBICAccount: string;
    selectsourceName: string;
    selectSourceEmail: string;
    selectSourceCurrency: string;
    selectSourceCurrencyP2Any: string;
    sourceBalance: number;
    sourceBalanceP2Any: number;
    beneficiaryNameP2Any: string;
    beneficiarycurrency: string;
    tID: string;
    beneficiarySelectedEmail: string;
    beneficiaryBankName: string;
    networkType: string
}

enum FundsTransferTypeEnum {
    PartyToPartyTransfer,
    PartyToAnyIBANTransfer,
}

interface TransferFundsDialogueState {
    lastRoute: string;
    SendPaymentType: FundsTransferTypeEnum;
    loading: boolean;
    btnLoader: boolean;
    isSubmitting: boolean;
    Bic: boolean;
    successAlert: boolean;
    beneficiaryAccountsOptions: ExtendedSelectItem[];
    beneficiaryIBANOptions: ExtendedSelectItem[];
    beneficiaryList: IndividualList[];
    beneficiaryWallets: Wallets[];
    paymentMethods: MerchantPaymentMethod[];
    SendRequestDetails: MoneyTransferRequestDetails | null;
    sourceOfFundsAreRequired: boolean;
    activeIndex: number;
    selectedBeneficiaryId: number;
    tID: string;
}

const validationSchema = Yup.object().shape({
    selectsourceAccount: Yup.string().required("Please select source account number."),
    amount: Yup.string()
        .required("Please enter amount.")
        .test("isValidAmount", "Please enter a valid amount, maximum.", (inputAmount) => {
            const amountValue = Number(inputAmount.replace(",", ""));
            return amountValue <= Withdraw_Max_Amount && Amount_Valid_REGEX.test(inputAmount);
        }),
    // .test("isSufficientBalance", "You have insufficient funds in your wallet. Please choose another account.", (inputAmount, { parent }) => {
    //     const amountValue = Number(inputAmount.replace(",", ""));
    //     return amountValue <= parent.sourceBalance;
    // }),
    paymentnote: Yup.string()
        .required("Please enter payment notes.")
        .max(Notes_And_Reason_Length, "Notes and reasons must not exceed " + Notes_And_Reason_Length + " characters."),

    selectbeneficiaryAccount: Yup.string().required("Please select beneficiary account number."),
    selectbeneficiaryIBANAccount: Yup.string().required("Please select beneficiary IBAN account number."),
    // networkType: Yup.string().required("Please select Network type.")
});

const validationSchemaP2Any = Yup.object().shape({
    selectSourceIBANP2Any: Yup.string().required("Please select source account number."),
    amountP2Any: Yup.string()
        .required("Please enter amount.")
        .test("isValidAmount", "Please enter a valid amount, maximum.", (inputAmount) => {
            const amountValue = Number(inputAmount.replace(",", ""));
            return amountValue <= Withdraw_Max_Amount && Amount_Valid_REGEX.test(inputAmount);
        }),
    // .test("isSufficientBalance", "You have insufficient funds in your wallet. Please choose another account.", (inputAmount, { parent }) => {
    //     const amountValue = Number(inputAmount.replace(",", ""));
    //     return amountValue <= parent.sourceBalanceP2Any;
    // }),
    paymentnoteP2Any: Yup.string()
        .required("Please enter payment notes.")
        .max(Notes_And_Reason_Length, "Notes and reasons must not exceed " + Notes_And_Reason_Length + " characters."),
    beneficiaryNameP2Any: Yup.string().required("Please enter beneficiary account name."),
    beneficiaryIBANAccount: Yup.string()
        .required("Please enter beneficiary IBAN account number.")
        .test("iban-validation", "Please enter valid IBAN account number.", (value) => {
            if (!value) {
                return false;
            }
            return utils.validateIban(value);
        }),
    beneficiaryBICAccount: Yup.string().required("Please enter beneficiary BIC account number."),
    beneficiaryBankName: Yup.string().required("Please enter beneficiary Bank name."),
    networkType: Yup.string().required("Please select Network type.")
});


export class TransferFundsDialogue extends Component<TransferFundsDialogueProps, TransferFundsDialogueState> {
    formikInitialValue: TransferFundsModal;
    NetworkTypes: SelectItem[] = [
        {
            label: "BISERA",
            value: NetworkType.Bulk
        },
        {
            label: "INST",
            value: NetworkType.Instant
        },
        {
            label: "VIREMENT",
            value: NetworkType.Virement
        }
    ];

    constructor(props: TransferFundsDialogueProps) {
        super(props);
        this.state = {
            activeIndex: 0,
            lastRoute: "",
            SendPaymentType: FundsTransferTypeEnum.PartyToPartyTransfer,
            loading: true,
            btnLoader: false,
            isSubmitting: false,
            Bic: false,
            successAlert: false,
            beneficiaryAccountsOptions: [],
            beneficiaryIBANOptions: [],
            beneficiaryList: [],
            beneficiaryWallets: [],
            paymentMethods: [],
            SendRequestDetails: null,
            sourceOfFundsAreRequired: false,
            selectedBeneficiaryId: 0,
            tID: "",
        };
        this.formikInitialValue = {
            selectsourceAccount: "",
            selectSourceIBANP2Any: "",
            selectbeneficiaryAccount: "",
            selectbeneficiaryIBANAccount: "",
            amount: "",
            paymentnote: "",
            amountP2Any: "",
            paymentnoteP2Any: "",
            beneficiaryName: "",
            beneficiaryIBANAccount: "",
            beneficiaryBICAccount: "",
            selectsourceName: "",
            selectSourceEmail: "",
            selectSourceCurrency: "",
            sourceBalance: 0,
            sourceBalanceP2Any: 0,
            beneficiarycurrency: "",
            tID: "",
            beneficiarySelectedEmail: "",
            beneficiaryBankName: "",
            beneficiaryNameP2Any: "",
            selectSourceCurrencyP2Any: "",
            networkType: ""
        };
    }

    async componentDidMount(): Promise<void> {
        await this.loadData();
    }

    componentWillUnmount(): void {
    }

    loadData = async () => {
        this.setState({ loading: true });
        await this.getMerchantBeneficiaries();
        this.setState({ loading: false });
    };

    // getSourceWallets = async () => {

    //     const walletsResult = await merchantWalletApiService.getOwnWallets();
    //     if (!walletsResult.success) {

    //         this.setState({ loading: false });
    //         this.toast?.show({ severity: "error", summary: "Error", detail: walletsResult.message, life: 3000 });
    //         return;
    //     }
    //     const sourceAccountOptions = walletsResult.data.map((wallet) => ({
    //         label: wallet.ibanAccountNumber,
    //         value: wallet.ibanAccountNumber,
    //         details: { icons: "wallet-outline", currency: wallet.currency }
    //     })) as SelectItem[];

    //     this.setState((prevState) => ({
    //         ...prevState,
    //         sourceAccountsOptions: sourceAccountOptions,
    //         sourceWallets: walletsResult.data
    //     }));
    // };

    getMerchantBeneficiaries = async () => {
        const beneficiaryListResult = await userApiService.getActiveIndividual();
        if (!beneficiaryListResult.success) {
            this.setState({ loading: false });
            toastService.error(beneficiaryListResult.message);
            return;
        }

        const filteredBeneficiaries = beneficiaryListResult.data.filter((beneficiary) => beneficiary.status === "Active");

        const beneficiaryDropDown = await Promise.all(
            filteredBeneficiaries.map(async (beneficiary) => {
                //const photoResult = beneficiary.photo ? await this.getIndividualBeneficiaryPhoto(beneficiary.photo) : null;
                return {
                    label: beneficiary.email,
                    value: beneficiary.id.toString(),
                    details: {
                        fullname: beneficiary.firstName + " " + beneficiary.lastName,
                        //image: photoResult,
                        showimage: true,
                    },
                } as ExtendedSelectItem;
            }),
        );

        this.setState({
            beneficiaryAccountsOptions: beneficiaryDropDown,
            beneficiaryList: beneficiaryListResult.data,
        });
    };

    itemTemplate = (option: ExtendedSelectItem) => {
        return (
            <div>
                <div className="text-primary">{option.details?.fullname}</div>
                <small className="text-muted">{option.label}</small>
            </div>
        );
    };

    getWalletBalance = async (accountNumber: string) => {
        const merchantBalanceRequest: MerchantWalletBalanceRequest = {
            merchantId: this.props.merchantId,
            accountNumber: accountNumber,
        };
        const balanceResponse = await merchantWalletApiService.getWalletBalance(merchantBalanceRequest);
        if (!balanceResponse.success) {
            this.setState({ loading: false });
            toastService.error(balanceResponse.message);
            return;
        }
        return balanceResponse.data;
    };

    setBeneficiaryWalletDetails = async (
        Wallets: Wallets[],
        beneficiaryid: string,
        sourceCurrency: string,
        selectedSourceIban: string,
        setFieldValue: FormikHelpers<TransferFundsModal>["setFieldValue"],
        values: TransferFundsModal,
    ) => {
        if (!utils.isNullOrEmptyString(selectedSourceIban)) {
            if (!utils.isNullOrEmptyString(beneficiaryid)) {
                const beneficiaryWalletFilterdWallets = Wallets.filter((x) => x.currency === sourceCurrency);
                if (beneficiaryWalletFilterdWallets.length > 0) {
                    this.setState({
                        beneficiaryIBANOptions: beneficiaryWalletFilterdWallets.map((wallet) => ({
                            label: wallet.ibanAccountNumber,
                            value: wallet.ibanAccountNumber,
                        })) as SelectItem[],
                    });
                    setFieldValue("selectbeneficiaryAccount", beneficiaryid);
                    setFieldValue("beneficiarycurrency", beneficiaryWalletFilterdWallets[0].currency ?? "");
                    setFieldValue("selectbeneficiaryIBANAccount", beneficiaryWalletFilterdWallets[0].ibanAccountNumber ?? "");
                } else {
                    this.setState({ beneficiaryIBANOptions: [] });
                    setFieldValue("selectbeneficiaryIBANAccount", "");
                    setFieldValue("beneficiarycurrency", "");
                    toastService.error("Beneficiary does not have same currency account");
                }
            }
        }
    };

    handleSourceAccountChange = async (
        e: { value: string },
        setFieldValue: FormikHelpers<TransferFundsModal>["setFieldValue"],
        values: TransferFundsModal,
    ) => {
        this.setState((prevState) => ({ ...prevState, loading: true }));
        const response = await this.getWalletBalance(this.props.sourceWallets.find((i) => i.ibanAccountNumber === e.value)?.id ?? "");
        if (response == null) {
            this.setState((prevState) => ({ ...prevState, loading: false }));
            return;
        }
        setFieldValue("selectSourceCurrency", response?.currency ?? "");
        setFieldValue("sourceBalance", response?.balance ?? 0);
        await this.setBeneficiaryWalletDetails(
            this.state.beneficiaryWallets,
            values.selectbeneficiaryAccount,
            response?.currency,
            e.value,
            setFieldValue,
            values,
        );
        this.setState((prevState) => ({ ...prevState, loading: false }));
    };

    handleSourceAccountChangeP2Any = async (
        e: { value: string },
        setFieldValue: FormikHelpers<TransferFundsModal>["setFieldValue"],
        values: TransferFundsModal,
    ) => {
        this.setState((prevState) => ({ ...prevState, loading: true }));
        const response = await this.getWalletBalance(this.props.sourceWallets.find((i) => i.ibanAccountNumber === e.value)?.id ?? "");
        if (response == null) {
            this.setState((prevState) => ({ ...prevState, loading: false }));
            return;
        }
        setFieldValue("selectSourceCurrencyP2Any", response?.currency ?? "");
        setFieldValue("sourceBalanceP2Any", response?.balance ?? 0);
        this.setState((prevState) => ({ ...prevState, loading: false }));
    };

    getIndividualBeneficiaryPhoto = async (imageId: string) => {
        const userPhoto = await fileService.GetFile(imageId);
        if (!userPhoto.success) {
            toastService.error(userPhoto.message);
            return;
        }
        return utils.appendBase64Data(userPhoto.data);
    };

    onSourceOfFundsUpload = async (documents: KycDocument[]) => {
        if (documents.length === 0) {
            toastService.error("Please select documents.");
            return;
        }

        this.setState({ isSubmitting: true });

        const promises = documents
            .filter((a) => a.objectURL !== "1")
            .map(async (a) => {
                const result = await moneyTransferApiService.uploadSourceOfFunds({
                    sourceEntityType: TennEntityType.Merchant,
                    sourceEntityId: storageService.getLoggedInUserMerchantId(),
                    transferId: this.state.SendRequestDetails?.transferId!,
                    fileName: a.fileName,
                    fileData: a.fileData
                } as SourceOfFundsUploadRequest);

                a.objectURL = result.success ? "1" : "";
                return result;
            });

        const results = await Promise.all(promises);
        const failedResults = results.filter((r) => !r.success);

        if (failedResults.length > 0) {
            this.setState({ isSubmitting: false });
            toastService.error("Uploading failed");

            return;
        }

        const submitResult = await moneyTransferApiService.notifySourceOfFundsUploaded({
            sourceEntityType: TennEntityType.Merchant,
            sourceEntityId: storageService.getLoggedInUserMerchantId(),
            transferId: this.state.SendRequestDetails?.transferId
        } as BankTransferRequestModel);

        if (!submitResult.success) {
            this.setState({ isSubmitting: false });
            toastService.error(submitResult.message);
            return;
        }

        this.setState({ isSubmitting: false, sourceOfFundsAreRequired: false });
    };


    onContinue = async (data: TransferFundsModal) => {
        let transactioDetails = {} as MoneyTransferRequestDetails;
        transactioDetails = {
            sourceCustomerEmail: data.selectSourceEmail,
            sourceCustomerName: data.selectsourceName,
            sourceCustomerCurrency: data.selectSourceCurrency,
            beneficiaryCustomerAccount: this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.selectbeneficiaryIBANAccount : data.beneficiaryIBANAccount,
            beneficiaryCustomerEmail: data.beneficiarySelectedEmail,
            beneficiaryCustomerCurrency: data.beneficiarycurrency,
            sourceCustomerAccount:
                this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.selectsourceAccount : data.selectSourceIBANP2Any,
            transactionAmount: this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.amount : data.amountP2Any,
            paymentNotes: this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.paymentnote : data.paymentnoteP2Any,
            sourceCurrentBalance:
                this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.sourceBalance : data.sourceBalanceP2Any,
            beneficiaryName:
                this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? data.beneficiaryName : data.beneficiaryNameP2Any,
        };

        // if (this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer) {
        //     const calculateMoneyTransferChargeRequest: CalculateMoneyTransferChargeRequest = {
        //         customerAccount: transactioDetails.sourceCustomerAccount,
        //         amount: Number(transactioDetails.transactionAmount),
        //         id: this.props.loggedUserData?.id ?? 0,
        //         PaymentType: PaymentType.P2P,
        //     };
        //     const calculateMoneyTransferChargeReponse = await merchantPaymentApiService.getMoneyTransferCharge(calculateMoneyTransferChargeRequest);

        //     if (!calculateMoneyTransferChargeReponse.success) {
        //         this.setState({ btnLoader: false });
        //         this.toast?.show({ severity: "error", summary: "Error", detail: calculateMoneyTransferChargeReponse.message, life: 3000 });
        //         return;
        //     }
        //     transactioDetails.transactionFee = calculateMoneyTransferChargeReponse.data.transactionFee;

        //     this.setState({ btnLoader: false, SendRequestDetails: transactioDetails });
        //     return;
        // }

        try {
            var moneyTransferObject: BankTransferInitiateRequestModel = {
                sourceEntityType: TennEntityType.Merchant,
                transferType: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? TransferType.ThirdPartyTransfer : TransferType.P2P,
                requestChannel: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? ServiceClients.MerchantPayment : ServiceClients.WalletApp10n,
                beneficiaryEntityType: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? TennEntityType.Unknown : TennEntityType.Individual,
                sourceEntityId: storageService.getLoggedInUserMerchantId(),
                sourceCustomerIban: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? data.selectSourceIBANP2Any : data.selectsourceAccount,
                beneficiaryCustomerIban: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? data.beneficiaryIBANAccount : data.selectbeneficiaryIBANAccount,
                beneficiaryCustomerBic: data.beneficiaryBICAccount,
                beneficiaryCustomerName: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? data.beneficiaryNameP2Any : data.beneficiaryName,
                amount: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? Number(data.amountP2Any) : Number(data.amount),
                paymentNotes: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? data.paymentnoteP2Any : data.paymentnote,
                // currency: data.selectSourceCurrency,
                beneficiaryEntityId: this.state.SendPaymentType == FundsTransferTypeEnum.PartyToAnyIBANTransfer ? 0 : Number(data.selectbeneficiaryAccount),
                network: this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer ? NetworkType.Internal : data.networkType
            };

            const moneyTransferInitiate = await moneyTransferApiService.moneyTransferInititate(moneyTransferObject);

            console.info("After toAnyIbanMoneyTransferInitiateAsync. Result: ", JSON.stringify(moneyTransferInitiate));

            if (!moneyTransferInitiate.success) {
                console.info("toAnyIbanMoneyTransferInitiateAsync Error: ", moneyTransferInitiate.message);
                toastService.error(moneyTransferInitiate.message);
                this.setState({ btnLoader: false });
                return;
            }
            if (moneyTransferInitiate.data.responseCode === BankTransferResponseCode.RiskReject) {
                toastService.error(moneyTransferInitiate.message);
                console.info("toAnyIbanMoneyTransferInitiateAsync - Risk reject");
                this.setState({ btnLoader: false });
                return;
            }
            transactioDetails.transactionFee = moneyTransferInitiate.data.chargeAmount;
            transactioDetails.transferId = moneyTransferInitiate.data.transferId;
            transactioDetails.sourceCustomerCurrency = moneyTransferInitiate.data.chargeCurrency;
            this.setState({ SendRequestDetails: transactioDetails, btnLoader: false, sourceOfFundsAreRequired: moneyTransferInitiate.data.responseCode === BankTransferResponseCode.SourceOfFundsRequired });
        } catch (error) {
            console.error("Error during API call: ", error);
        }
    };

    onSubmit = async () => {
        console.log("Point to point submitting----");
        try {
            this.setState({ isSubmitting: true });

            if (this.state.SendPaymentType === FundsTransferTypeEnum.PartyToPartyTransfer) {
                var p2pObject: PointToPointMoneyTransferRequestModel = {
                    sourceCustomerAccount: this.state.SendRequestDetails?.sourceCustomerAccount!,
                    beneficiaryCustomerAccount: this.state.SendRequestDetails?.beneficiaryCustomerAccount!,
                    transactionAmount: Number(this.state.SendRequestDetails?.transactionAmount),
                    paymentNotes: this.state.SendRequestDetails?.paymentNotes,
                };

                const p2pSendMoneyResponse = await merchantPaymentApiService.toEndUserMoneyTransfer(
                    this.props.loggedUserData?.id ?? 0,
                    Number(this.state.selectedBeneficiaryId),
                    p2pObject,
                );

                if (!p2pSendMoneyResponse.success) {
                    this.setState({ isSubmitting: false });
                    toastService.error(p2pSendMoneyResponse.message);
                    return;
                }

                this.setState({ SendRequestDetails: null, isSubmitting: false, successAlert: true, tID: p2pSendMoneyResponse.data });
            }
        } catch (error) {
            console.log("Exception occure in Point to point submitting----", error);
            this.setState({ isSubmitting: false });
        }
    };

    onSubmitExecute = async () => {
        this.setState({ isSubmitting: true });
        var p2AnyIABNObject: BankTransferRequestModel = {
            sourceEntityType: TennEntityType.Merchant,
            sourceEntityId: storageService.getLoggedInUserMerchantId(),
            transferId: this.state.SendRequestDetails?.transferId ?? 0
        };

        const moneyTransferExecuteResponse = await moneyTransferApiService.moneyTransferExecute(p2AnyIABNObject);

        if (!moneyTransferExecuteResponse.success) {
            this.setState({ isSubmitting: false, SendRequestDetails: null, });
            toastService.error(moneyTransferExecuteResponse.message);
            return;
        }

        if (moneyTransferExecuteResponse.data.responseCode == BankTransferResponseCode.RiskReject) {
            this.setState(
                { isSubmitting: false, SendRequestDetails: null },
                () => {
                    toastService.error(moneyTransferExecuteResponse.message ?? "Rejected by Risk Reasons");
                }
            );
            return;
        }
        if (moneyTransferExecuteResponse.data.responseCode == BankTransferResponseCode.UnderReiew) {
            this.setState(
                { isSubmitting: false, SendRequestDetails: null },
                () => {
                    toastService.error(moneyTransferExecuteResponse.message ?? "Send for evaluation from Compliance.");
                }
            );
            return;
        }
        this.props.reloadData();
        this.setState({ SendRequestDetails: null, isSubmitting: false, successAlert: true, tID: moneyTransferExecuteResponse.data.transactionId });
    }

    handleBeneficiaryAccountChange = async (
        beneficiaryAccount: string,
        setFieldValue: FormikHelpers<TransferFundsModal>["setFieldValue"],
        values: TransferFundsModal,
        beneficiaryId: number,
    ) => {
        this.setState({ loading: true });
        try {
            const beneficiaryWalletResult = await userWalletApiService.getWallets(Number(beneficiaryAccount));
            if (!beneficiaryWalletResult.success) {
                this.setState({ beneficiaryIBANOptions: [] });
                this.setState({ loading: false });
                setFieldValue("beneficiarycurrency", "");
                setFieldValue("selectbeneficiaryIBANAccount", "");
                toastService.error(beneficiaryWalletResult.message);
                return;
            }
            if (beneficiaryWalletResult.data.length <= 0) {
                this.setState({ beneficiaryIBANOptions: [] });
                this.setState({ loading: false });
                setFieldValue("beneficiarycurrency", "");
                setFieldValue("selectbeneficiaryIBANAccount", "");
                toastService.error("Beneficery wallets not found.");
                return;
            }

            await this.setBeneficiaryWalletDetails(
                beneficiaryWalletResult.data,
                beneficiaryAccount,
                values.selectSourceCurrency,
                values.selectsourceAccount,
                setFieldValue,
                values,
            );
            this.setState({ loading: false, selectedBeneficiaryId: beneficiaryId });
        } catch (error) {
            this.setState({ loading: false });
            toastService.error("An error occurred while fetching the beneficiary account details.");
        }
    };

    handleSubmit = async (values: TransferFundsModal) => {
        this.setState({ btnLoader: true });
        await this.onContinue(values);
        this.setState({ btnLoader: false });
    };

    setValue = async (e: number, mode: FundsTransferTypeEnum) => {
        this.setState({ activeIndex: e, SendPaymentType: mode });
    };

    handleTabChange = async (event: TabViewTabChangeEvent, formikError: any, formikTouched: any) => {
        await formikError({});
        await formikTouched({});
        if (event.index == 0) {
            await this.setValue(event.index, FundsTransferTypeEnum.PartyToPartyTransfer);
        }
        if (event.index == 1) {
            await this.setValue(event.index, FundsTransferTypeEnum.PartyToAnyIBANTransfer);
        }
    };

    renderLaoding = () => {
        if (this.state.loading) {
            return (
                <LoadingDots />
            );
        }
    };

    renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center w-100 mt-4 ml-2">
                <ArrowLeft
                    size={16}
                    style={{
                        cursor: 'pointer',
                        color: 'inherit'
                    }}
                    onClick={() => this.setState({ successAlert: false })}
                />
                <div className="flex-grow-1 text-center font-bold ml-4 ">
                    Success
                </div>
            </div>
        );
    };

    render() {
        return (
            <Dialog
                header={this.state.successAlert ? this.renderHeader : "Transfer Funds"}
                draggable={false}
                resizable={false}
                visible={true}
                style={{ width: "70vw" }}
                onHide={this.props.onHide}
                contentStyle={{ maxHeight: "70vh", overflowY: "auto" }}
            >
                {!this.state.successAlert && (
                    <Formik
                        validateOnChange={true}
                        validateOnBlur={true}
                        initialValues={this.formikInitialValue}
                        validationSchema={
                            this.state.SendPaymentType == FundsTransferTypeEnum.PartyToPartyTransfer ? validationSchema : validationSchemaP2Any
                        }
                        onSubmit={async (values, { setSubmitting }) => {
                            await this.handleSubmit(values);
                            setSubmitting(false);
                        }}
                    >
                        {({ values, errors, touched, handleSubmit, setFieldValue, setErrors, setTouched }) => (
                            <>
                                <TabView
                                    activeIndex={this.state.activeIndex}
                                    onTabChange={(e) => this.handleTabChange(e, setErrors, setTouched)}
                                >
                                    <TabPanel header="Wallet Transfer" headerTemplate={createTabHeaderTemplate(this.state.activeIndex)}>
                                        <>
                                            {this.renderLaoding()}

                                            {values.selectSourceCurrency && (
                                                <div className="d-flex justify-content-end align-items-end">
                                                    <p>
                                                        Bal:{" "}
                                                        <span className="text-success">
                                                            {utils.formatBalance(values.sourceBalance, values.selectSourceCurrency)}
                                                        </span>
                                                    </p>
                                                </div>
                                            )}

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Source Account</Label>
                                                        <Dropdown
                                                            disabled={this.state.btnLoader}
                                                            appendTo={document.body}
                                                            value={values.selectsourceAccount}
                                                            onChange={(e) => {
                                                                const sourceDetails = this.props.sourceAccountsOptions.find(
                                                                    (i) => i.value == e.value,
                                                                )?.details;
                                                                setFieldValue("selectsourceName", sourceDetails?.fullname);
                                                                setFieldValue("selectSourceEmail", sourceDetails?.subtitle);
                                                                setFieldValue("selectsourceAccount", e.value);
                                                                this.handleSourceAccountChange(e, setFieldValue, values);
                                                            }}
                                                            name="selectsourceAccount"
                                                            options={this.props.sourceAccountsOptions}
                                                            required={true}
                                                            className={`w-full ${errors.selectsourceAccount && touched.selectsourceAccount ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="selectsourceAccount" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Network</Label>
                                                        <Dropdown
                                                            disabled={true}
                                                            appendTo={document.body}
                                                            value={NetworkType.Internal}
                                                            onChange={(e) => setFieldValue("networkType", e.value)}
                                                            name="networkType"
                                                            // options={this.NetworkTypes}
                                                            options={[{ label: NetworkType.Internal, value: NetworkType.Internal }]}
                                                            required={true}
                                                            className={`w-full ${errors.networkType && touched.networkType ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="networkType" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Beneficiary</Label>
                                                        <Dropdown
                                                            disabled={values.selectsourceAccount == "" || this.state.btnLoader}
                                                            appendTo={document.body}
                                                            value={values.selectbeneficiaryAccount}
                                                            onChange={(e) => {
                                                                const beneficiaryDetails = this.state.beneficiaryList.find(
                                                                    (f) => f.id == Number(e.value),
                                                                );
                                                                this.handleBeneficiaryAccountChange(
                                                                    e.value,
                                                                    setFieldValue,
                                                                    values,
                                                                    beneficiaryDetails?.id ?? 0,
                                                                );
                                                                setFieldValue("beneficiarySelectedEmail", beneficiaryDetails?.email);
                                                                setFieldValue("selectbeneficiaryAccount", e.value);
                                                                setFieldValue(
                                                                    "beneficiaryName",
                                                                    beneficiaryDetails?.firstName + " " + beneficiaryDetails?.lastName,
                                                                );
                                                            }}
                                                            name="selectbeneficiaryAccount"
                                                            options={this.state.beneficiaryAccountsOptions}
                                                            required={true}
                                                            filter
                                                            className={`w-full ${errors.selectbeneficiaryAccount && touched.selectbeneficiaryAccount ? "p-invalid" : ""}`}
                                                            itemTemplate={this.itemTemplate}
                                                        />
                                                        <ErrorMessage component="small" name="selectbeneficiaryAccount" className="p-error" />
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Beneficiary Account</Label>
                                                        <Dropdown
                                                            appendTo={document.body}
                                                            value={values.selectbeneficiaryIBANAccount}
                                                            onChange={(e) => setFieldValue("selectbeneficiaryIBANAccount", e.value)}
                                                            name="selectbeneficiaryIBANAccount"
                                                            options={this.state.beneficiaryIBANOptions}
                                                            required={true}
                                                            className={`w-full ${errors.selectbeneficiaryIBANAccount && touched.selectbeneficiaryIBANAccount ? "p-invalid" : ""}`}
                                                            disabled={values.selectsourceAccount == "" || this.state.btnLoader}
                                                        />
                                                        <ErrorMessage
                                                            component="small"
                                                            name="selectbeneficiaryIBANAccount"
                                                            className="p-error"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <FormGroup>
                                                <Label>Amount</Label>
                                                <InputText
                                                    style={{ backgroundColor: "transparent" }}
                                                    required={true}
                                                    value={values.amount}
                                                    onChange={(inputValue) => {
                                                        const formattedAmount = utils.formatAmount(inputValue.target.value);
                                                        setFieldValue("amount", formattedAmount);
                                                    }}
                                                    readOnly={values.selectsourceAccount == "" || this.state.btnLoader}
                                                    name="amount"
                                                    className={`w-full ${errors.amount && touched.amount ? "p-invalid" : ""}`}
                                                    maxLength={10}
                                                />
                                                <ErrorMessage component="small" name="amount" className="p-error" />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Payment Notes</Label>
                                                <InputTextarea
                                                    required={true}
                                                    value={values.paymentnote}
                                                    onChange={(value) => setFieldValue("paymentnote", value.target.value)}
                                                    readOnly={values.selectsourceAccount == "" || this.state.btnLoader}
                                                    name="paymentnote"
                                                    className={`w-full ${errors.paymentnote && touched.paymentnote ? "p-invalid" : ""}`}
                                                    maxLength={AppConstants.NotesAndReasonLength}
                                                />
                                                <ErrorMessage component="small" name="paymentnote" className="p-error" />
                                            </FormGroup>

                                            <div className="d-flex justify-content-end align-items-end">
                                                <div>
                                                    <Button
                                                        type="button"
                                                        iconPos="right"
                                                        label="Continue"
                                                        onClick={() => handleSubmit()}
                                                        loading={this.state.btnLoader}
                                                        disabled={this.state.btnLoader}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    </TabPanel>
                                    <TabPanel header="External IBAN Transfer" headerTemplate={createTabHeaderTemplate(this.state.activeIndex)}>
                                        <>
                                            {this.renderLaoding()}

                                            {values.selectSourceCurrencyP2Any && (
                                                <div className="d-flex justify-content-end align-items-end">
                                                    <p>
                                                        Bal:{" "}
                                                        <span className="text-success">
                                                            {utils.formatBalance(values.sourceBalanceP2Any, values.selectSourceCurrencyP2Any)}
                                                        </span>
                                                    </p>
                                                </div>
                                            )}

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select source Account</Label>
                                                        <Dropdown
                                                            appendTo={document.body}
                                                            value={values.selectSourceIBANP2Any}
                                                            onChange={(e) => {
                                                                const sourceDetails = this.props.sourceAccountsOptions.find(
                                                                    (i) => i.value == e.value,
                                                                )?.details;
                                                                setFieldValue("selectSourceName", sourceDetails?.fullname);
                                                                setFieldValue("selectSourceIBANP2Any", e.value);
                                                                this.handleSourceAccountChangeP2Any(e, setFieldValue, values);
                                                            }}
                                                            name="selectSourceIBANP2Any"
                                                            options={this.props.sourceAccountsOptions}
                                                            required={true}
                                                            filter
                                                            className={`w-full ${errors.selectSourceIBANP2Any && touched.selectSourceIBANP2Any ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="selectSourceIBANP2Any" className="p-error" />
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Network</Label>
                                                        <Dropdown
                                                            disabled={this.state.btnLoader}
                                                            appendTo={document.body}
                                                            value={values.networkType}
                                                            onChange={(e) => setFieldValue("networkType", e.value)}
                                                            name="networkType"
                                                            options={this.NetworkTypes}
                                                            required={true}
                                                            className={`w-full ${errors.networkType && touched.networkType ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="networkType" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>



                                            <FormGroup>
                                                <Label>Beneficiary Name</Label>
                                                <InputText
                                                    style={{ backgroundColor: "transparent" }}
                                                    required={true}
                                                    value={values.beneficiaryNameP2Any}
                                                    onChange={(inputValue) => {
                                                        setFieldValue("beneficiaryNameP2Any", inputValue.target.value);
                                                    }}
                                                    readOnly={this.state.btnLoader}
                                                    name="beneficiaryNameP2Any"
                                                    className={`w-full ${errors.beneficiaryNameP2Any && touched.beneficiaryNameP2Any ? "p-invalid" : ""}`}
                                                    maxLength={AppConstants.NameLength}
                                                />
                                                <ErrorMessage component="small" name="beneficiaryNameP2Any" className="p-error" />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Beneficiary IBAN</Label>
                                                <InputText
                                                    style={{ backgroundColor: "transparent" }}
                                                    required={true}
                                                    value={values.beneficiaryIBANAccount}
                                                    onChange={(inputValue) => {
                                                        setFieldValue("beneficiaryIBANAccount", inputValue.target.value);
                                                        setFieldValue("beneficiaryBICAccount", "");
                                                        setFieldValue("beneficiaryBankName", "");
                                                    }}
                                                    readOnly={this.state.btnLoader}
                                                    name="beneficiaryIBANAccount"
                                                    className={`w-full ${errors.beneficiaryIBANAccount && touched.beneficiaryIBANAccount ? "p-invalid" : ""}`}
                                                    maxLength={AppConstants.IbanMaxLength}
                                                    onBlur={async () => {
                                                        //handleBlur("beneficiaryIBANAccount");
                                                        if (values.beneficiaryIBANAccount.length > 14) {
                                                            this.setState({ loading: true });
                                                            const obj: ValidateIbanRequest = {
                                                                iban: values.beneficiaryIBANAccount,
                                                            };
                                                            var validIban = await userApiService.validateIban(obj);
                                                            if (!validIban.success) {
                                                                setFieldValue("beneficiaryBICAccount", "");
                                                                setFieldValue("beneficiaryBankName", "");
                                                                this.setState({ loading: false });
                                                                // toast.error(t(utils.removeBrackets(validIban.message)));
                                                                return;
                                                            }
                                                            setFieldValue(
                                                                "beneficiaryBICAccount",
                                                                validIban.data.bic.endsWith("XXX")
                                                                    ? validIban.data.bic.slice(0, -3)
                                                                    : validIban.data.bic,
                                                            );
                                                            setFieldValue("beneficiaryBankName", validIban.data.bank);
                                                            this.setState({ loading: false });
                                                        }
                                                    }}
                                                />
                                                <ErrorMessage component="small" name="beneficiaryIBANAccount" className="p-error" />
                                            </FormGroup>

                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label>Beneficiary BIC</Label>
                                                        <InputText
                                                            style={{ backgroundColor: "transparent" }}
                                                            required={true}
                                                            value={values.beneficiaryBICAccount}
                                                            onChange={(inputValue) => {
                                                                setFieldValue("beneficiaryBICAccount", inputValue.target.value);
                                                            }}
                                                            readOnly={true}
                                                            name="beneficiaryBICAccount"
                                                            className={`w-full ${errors.beneficiaryBICAccount && touched.beneficiaryBICAccount ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="beneficiaryBICAccount" className="p-error" />
                                                    </FormGroup>
                                                </Col>

                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label>Beneficiary Bank Name</Label>
                                                        <InputText
                                                            style={{ backgroundColor: "transparent" }}
                                                            required={true}
                                                            value={values.beneficiaryBankName}
                                                            onChange={(inputValue) => {
                                                                setFieldValue("beneficiaryBankName", inputValue.target.value);
                                                            }}
                                                            readOnly={true}
                                                            name="beneficiaryBankName"
                                                            className={`w-full ${errors.beneficiaryBankName && touched.beneficiaryBankName ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="beneficiaryBankName" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <FormGroup>
                                                <Label>Amount</Label>
                                                <InputText
                                                    style={{ backgroundColor: "transparent" }}
                                                    required={true}
                                                    value={values.amountP2Any}
                                                    onChange={(inputValue) => {
                                                        const formattedAmount = utils.formatAmount(inputValue.target.value);
                                                        setFieldValue("amountP2Any", formattedAmount);
                                                    }}
                                                    readOnly={values.selectSourceIBANP2Any == "" || this.state.btnLoader}
                                                    name="amountP2Any"
                                                    className={`w-full ${errors.amountP2Any && touched.amountP2Any ? "p-invalid" : ""}`}
                                                    maxLength={10}
                                                />
                                                <ErrorMessage component="small" name="amountP2Any" className="p-error" />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Payment Notes</Label>
                                                <InputTextarea
                                                    required={true}
                                                    value={values.paymentnoteP2Any}
                                                    onChange={(value) => setFieldValue("paymentnoteP2Any", value.target.value)}
                                                    readOnly={values.selectSourceIBANP2Any == "" || this.state.btnLoader}
                                                    name="paymentnoteP2Any"
                                                    className={`w-full ${errors.paymentnoteP2Any && touched.paymentnoteP2Any ? "p-invalid" : ""}`}
                                                    maxLength={AppConstants.NotesAndReasonLength}
                                                />
                                                <ErrorMessage component="small" name="paymentnoteP2Any" className="p-error" />
                                            </FormGroup>

                                            <div className="d-flex justify-content-end align-items-end">
                                                <div>
                                                    <Button
                                                        iconPos="right"
                                                        type="button"
                                                        label="Continue"
                                                        onClick={() => handleSubmit()}
                                                        loading={this.state.btnLoader}
                                                        disabled={this.state.btnLoader}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    </TabPanel>
                                </TabView>
                            </>
                        )}
                    </Formik>
                )}

                {this.state.SendRequestDetails != null && !this.state.sourceOfFundsAreRequired && (
                    <TransferFundsConfirmDialogue
                        transactionDetails={this.state.SendRequestDetails}
                        sourceSelectedCurrency={this.state.SendRequestDetails.sourceCustomerAccount}
                        beneficiarySelectedCurrency={this.state.SendRequestDetails.beneficiaryCustomerCurrency!}
                        isSubmitting={this.state.isSubmitting}
                        onSubmit={async () => await this.onSubmitExecute()
                        }
                        onHide={() => {
                            this.setState({ isSubmitting: false, SendRequestDetails: null });
                        }}
                        p2pTransfer={this.state.SendPaymentType == FundsTransferTypeEnum.PartyToPartyTransfer ? true : false}
                        p2AnyIbanTransfer={this.state.SendPaymentType === FundsTransferTypeEnum.PartyToAnyIBANTransfer}
                    />
                )}

                {this.state.successAlert && (
                    <MoneyTransferSuccessDialog transactionId={this.state.tID} />
                )}


                {this.state.SendRequestDetails != null && this.state.sourceOfFundsAreRequired && (
                    <UploadSourceOfFundsDialog
                        isSubmitting={this.state.isSubmitting}
                        onSubmit={this.onSourceOfFundsUpload}
                        onHide={() => {
                            this.setState({ isSubmitting: false, sourceOfFundsAreRequired: false, SendRequestDetails: null });
                        }} />
                )}


            </Dialog>
        );
    }
}
